/**
 * useLazyQuestions Hook
 * Provides lazy loading of exam questions during exam session.
 * Fetches questions on demand around the current question index.
 */

import { useState, useCallback, useRef } from 'react';
import { apiService } from '../lib/api';

interface LazyQuestion {
  id: string;
  questionId?: string;
  order: number;
  clusterId?: string;
  questionClusterId?: string;
  type?: string;
  context?: string;
  stem?: string;
  options?: any[];
  trueFalseItems?: any[];
  matchingPrompts?: any[];
  matchingOptions?: any[];
  fillBlankAnswers?: any[];
  [key: string]: any;
  isLoaded?: boolean; // Flag to indicate if full content is loaded
}

interface ClusterContext {
  clusterId: string;
  title?: string;
  content?: string;
  subject?: string;
  mediaType?: string;
  stimulusData?: {
    stimulusText?: string;
    content?: string;
    questionClusterId?: string;
  };
  stimulusDataMap?: Record<
    string,
    {
      stimulusText?: string;
      content?: string;
    }
  >;
  hasMultipleSubClusters?: boolean;
}

interface ExamInfo {
  id: string;
  title: string;
  subject: string;
  duration: number;
  totalQuestions: number;
  totalPoints?: number;
  description?: string;
  price?: number;
  passingScore?: number | null;
  standard?: string | null;
}

interface UseLazyQuestionsOptions {
  examId: string;
  /** How many questions to fetch before/after current (default: 2) */
  prefetchRange?: number;
  /** Called when session expires */
  onSessionExpired?: () => void;
  /** Called when time remaining is updated */
  onTimeUpdate?: (seconds: number | null) => void;
  /** Called when fetch fails */
  onError?: (error: Error) => void;
}

interface UseLazyQuestionsReturn {
  /** Currently loaded questions */
  questions: Map<number, LazyQuestion>;
  /** Cluster contexts keyed by clusterId */
  clusters: Map<string, ClusterContext>;
  /** Total number of questions in exam */
  totalQuestions: number;
  /** Current session ID */
  sessionId: string | null;
  /** Time remaining in seconds */
  timeRemaining: number | null;
  /** Whether data is currently being fetched */
  loading: boolean;
  /** Current error if any */
  error: Error | null;
  /** Loaded question range */
  loadedRange: { start: number; end: number } | null;
  /** Exam metadata from backend */
  examInfo: ExamInfo | null;
  /** Fetch questions around a specific index (1-indexed) */
  fetchQuestionsAround: (questionIndex: number) => Promise<void>;
  /** Get a specific question by its order (1-indexed), returns undefined if not loaded */
  getQuestion: (order: number) => LazyQuestion | undefined;
  /** Get cluster context for a question, handling multi-subcluster stimulusData */
  getClusterContextForQuestion: (question: LazyQuestion) => ClusterContext | undefined;
  /** Get stimulus data for a specific question (handles multi-subcluster) */
  getStimulusDataForQuestion: (
    question: LazyQuestion
  ) => { stimulusText?: string; content?: string } | undefined;
  /** Check if a question is loaded */
  isQuestionLoaded: (order: number) => boolean;
  /** Clear all cached questions */
  clearCache: () => void;
}

/**
 * Hook for lazy loading exam questions
 * Prevents loading entire exam at once for security
 */
export function useLazyQuestions(options: UseLazyQuestionsOptions): UseLazyQuestionsReturn {
  const { examId, prefetchRange = 2, onSessionExpired, onTimeUpdate, onError } = options;

  // Use Map to store questions by order (1-indexed)
  // We use refs for internal logic to avoid dependency cycles in useCallback
  const questionsRef = useRef<Map<number, LazyQuestion>>(new Map());
  const clustersRef = useRef<Map<string, ClusterContext>>(new Map());

  // State for UI updates
  const [questions, setQuestions] = useState<Map<number, LazyQuestion>>(new Map());
  const [clusters, setClusters] = useState<Map<string, ClusterContext>>(new Map());
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [loadedRange, setLoadedRange] = useState<{ start: number; end: number } | null>(null);
  const [examInfo, setExamInfo] = useState<ExamInfo | null>(null);

  // Track in-flight requests to avoid duplicates
  const pendingRequests = useRef<Set<string>>(new Set());

  // Sync refs with initial state if needed (though we start empty)
  // No need for explicit sync effect as we update both ref and state together

  /**
   * Fetch questions around a specific index
   */
  const fetchQuestionsAround = useCallback(
    async (questionIndex: number) => {
      const requestKey = `${questionIndex}-${prefetchRange}`;

      // Skip if already fetching this range
      if (pendingRequests.current.has(requestKey)) {
        return;
      }

      // Check if already loaded using Ref (avoiding dependency on state)
      const startNeeded = Math.max(1, questionIndex - prefetchRange);
      const endNeeded = questionIndex + prefetchRange;

      let allLoaded = true;
      const currentQuestions = questionsRef.current;

      for (let i = startNeeded; i <= Math.min(endNeeded, totalQuestions || Infinity); i++) {
        if (!currentQuestions.has(i) || !currentQuestions.get(i)?.isLoaded) {
          // Check isLoaded flag too
          allLoaded = false;
          break;
        }
      }

      // Skip fetch if all questions in range are already loaded
      // If totalQuestions is 0, we imply it's not loaded yet, so we proceed
      if (allLoaded && totalQuestions > 0) {
        return;
      }

      pendingRequests.current.add(requestKey);
      setLoading(true);
      setError(null);

      try {
        const response = await apiService.getSessionQuestions(examId, questionIndex, prefetchRange);

        // Update session info
        setSessionId(response.sessionId);
        setTotalQuestions(response.totalQuestions);
        setTimeRemaining(response.timeRemaining);

        // Notify time update
        if (onTimeUpdate) {
          onTimeUpdate(response.timeRemaining);
        }

        // Update exam info if provided
        if (response.examInfo) {
          setExamInfo(response.examInfo);
        }

        // Add fetched questions to cache (both Ref and State)
        const newQuestionsMap = new Map(questionsRef.current);
        const newClustersMap = new Map(clustersRef.current);

        // 1. Process Metadata Map (if available) - Populate Skeletons
        if (response.questionMap && Array.isArray(response.questionMap)) {
          response.questionMap.forEach((qMeta: any) => {
            if (!newQuestionsMap.has(qMeta.order)) {
              newQuestionsMap.set(qMeta.order, {
                ...qMeta,
                isLoaded: false,
              });
            }
          });
        }

        // 2. Process Full Content Questions - Overwrite Skeletons
        response.questions.forEach((q: LazyQuestion) => {
          newQuestionsMap.set(q.order, {
            ...q,
            isLoaded: true,
          });
        });

        // 3. Process Clusters
        Object.entries(response.clusters).forEach(([clusterId, context]) => {
          newClustersMap.set(clusterId, context as ClusterContext);
        });

        // Update Refs
        questionsRef.current = newQuestionsMap;
        clustersRef.current = newClustersMap;

        // Update State
        setQuestions(newQuestionsMap);
        setClusters(newClustersMap);

        // Update loaded range
        setLoadedRange(prev => {
          if (!prev) {
            return response.currentRange;
          }
          return {
            start: Math.min(prev.start, response.currentRange.start),
            end: Math.max(prev.end, response.currentRange.end),
          };
        });
      } catch (err) {
        const error = err as Error;

        // Check if session expired
        if (error.message?.includes('expired') || error.message?.includes('403')) {
          if (onSessionExpired) {
            onSessionExpired();
          }
        }

        setError(error);
        if (onError) {
          onError(error);
        }
      } finally {
        pendingRequests.current.delete(requestKey);
        setLoading(false);
      }
    },
    [examId, prefetchRange, totalQuestions, onTimeUpdate, onSessionExpired, onError] // questions removed from dependency
  );

  /**
   * Get a question by order (1-indexed)
   */
  const getQuestion = useCallback(
    (order: number): LazyQuestion | undefined => {
      // Return from state to ensure reactivity
      return questions.get(order);
    },
    [questions]
  );

  /**
   * Get cluster context for a question
   */
  const getClusterContextForQuestion = useCallback(
    (question: LazyQuestion): ClusterContext | undefined => {
      if (!question.clusterId) return undefined;
      return clusters.get(question.clusterId);
    },
    [clusters]
  );

  /**
   * Get stimulus data for a specific question
   * Handles both single and multiple sub-clusters
   */
  const getStimulusDataForQuestion = useCallback(
    (question: LazyQuestion): { stimulusText?: string; content?: string } | undefined => {
      const clusterContext = getClusterContextForQuestion(question);
      if (!clusterContext) return undefined;

      const questionClusterId = question.questionClusterId;

      // Check if this cluster has multiple sub-clusters
      if (clusterContext.hasMultipleSubClusters && clusterContext.stimulusDataMap) {
        // CRITICAL FIX: Only return stimulus if ID matches. Do NOT fall back.
        if (questionClusterId && clusterContext.stimulusDataMap[questionClusterId]) {
          return clusterContext.stimulusDataMap[questionClusterId];
        }
        return undefined;
      }

      // Single sub-cluster or no sub-clusters
      const stimulusData = clusterContext.stimulusData;
      if (!stimulusData) return undefined;

      // CRITICAL FIX: If stimulusData is specific to a sub-cluster, only return it if ID matches.
      if (typeof stimulusData === 'object' && stimulusData !== null) {
        const stimulusOwnerClusterId = (stimulusData as any).questionClusterId;
        if (stimulusOwnerClusterId) {
          if (!questionClusterId || questionClusterId !== stimulusOwnerClusterId) {
            return undefined;
          }
        }
      }

      return stimulusData;
    },
    [getClusterContextForQuestion]
  );

  /**
   * Check if question is loaded
   */
  const isQuestionLoaded = useCallback(
    (order: number): boolean => {
      return questions.has(order);
    },
    [questions]
  );

  /**
   * Clear all cached questions
   */
  const clearCache = useCallback(() => {
    setQuestions(new Map());
    setClusters(new Map());
    setLoadedRange(null);
  }, []);

  return {
    questions,
    clusters,
    totalQuestions,
    sessionId,
    timeRemaining,
    loading,
    error,
    loadedRange,
    examInfo,
    fetchQuestionsAround,
    getQuestion,
    getClusterContextForQuestion,
    getStimulusDataForQuestion,
    isQuestionLoaded,
    clearCache,
  };
}

export default useLazyQuestions;
